import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, ScrollText } from "lucide-react";
import { AdminPage } from "@/components/AdminPage";
import { api } from "@/lib/api";
import { describeEntry, type AuditEntry } from "@/lib/audit-describe";

/** Drops empty filters so a cleared field never sends an empty string. */
function buildQuery(f: {
  actor: string;
  action: string;
  entity: string;
  from: string;
  to: string;
  cursor?: string | null;
}) {
  return {
    actor: f.actor || undefined,
    action: f.action || undefined,
    entity: f.entity || undefined,
    from: f.from || undefined,
    to: f.to || undefined,
    cursor: f.cursor || undefined,
  };
}

function formatDateTime(iso: string): string {
  return new Date(iso.replace(" ", "T") + "Z").toLocaleString("pt-BR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

type Filters = {
  actor: string;
  action: string;
  entity: string;
  from: string;
  to: string;
};

const DEFAULT_FILTERS: Filters = { actor: "staff", action: "", entity: "", from: "", to: "" };

export default function AuditPage() {
  const [actionInput, setActionInput] = useState("");
  const [entityInput, setEntityInput] = useState("");
  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Only the newest request may write state. Without this, a slow query for
  // one filter can land after a fast query for another and leave the list
  // contradicting the filter on screen — unacceptable in a log someone reads
  // to answer "who did this". Bumped by both the page-1 load effect and
  // loadMore, so a filter change also invalidates any in-flight "load more".
  const requestId = useRef(0);

  // Free-text action/entity fields debounce into `filters` so filtering
  // does not fire a request per keystroke — same convention as AdminTable.
  // Bailing out when nothing actually changed avoids a redundant second
  // fetch on mount (the timeout would otherwise still produce a new
  // `filters` object reference and re-trigger the load effect below).
  useEffect(() => {
    const t = setTimeout(() => {
      setFilters((prev) => {
        const action = actionInput.trim();
        const entity = entityInput.trim();
        if (prev.action === action && prev.entity === entity) return prev;
        return { ...prev, action, entity };
      });
    }, 300);
    return () => clearTimeout(t);
  }, [actionInput, entityInput]);

  const load = useCallback(async (f: Filters) => {
    const id = ++requestId.current;
    setLoading(true);
    setError(null);
    try {
      const res = await api.request("GET /admin/audit", { query: buildQuery(f) });
      if (id !== requestId.current) return; // a newer request superseded us
      setEntries(res.items);
      setCursor(res.next_cursor);
    } catch {
      if (id !== requestId.current) return;
      setError("Não foi possível carregar o log de auditoria.");
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, []);

  // `filters` is a new object every time any field changes, so this also
  // resets the list and cursor for that new query — no stale cursor from a
  // previous filter combination can leak into the next request.
  useEffect(() => {
    void load(filters);
  }, [filters, load]);

  async function loadMore() {
    if (!cursor || loadingMore) return;
    const id = ++requestId.current;
    setLoadingMore(true);
    try {
      const res = await api.request("GET /admin/audit", { query: buildQuery({ ...filters, cursor }) });
      if (id !== requestId.current) return; // filters changed while this page 2 was in flight
      setEntries((prev) => [...prev, ...res.items]);
      setCursor(res.next_cursor);
    } catch {
      if (id !== requestId.current) return;
      setError("Não foi possível carregar mais registros.");
    } finally {
      if (id === requestId.current) setLoadingMore(false);
    }
  }

  function updateFilter<K extends keyof Filters>(key: K, value: Filters[K]) {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }

  const filterBar = (
    <div className="flex flex-wrap items-center gap-2">
      <select
        value={filters.actor}
        onChange={(e) => updateFilter("actor", e.target.value)}
        aria-label="Filtrar por autor"
        className="rounded-lg border border-border bg-surface px-3 py-1.5 text-xs text-text-secondary outline-none focus:border-accent"
      >
        <option value="staff">Equipe</option>
        <option value="system">Sistema</option>
        <option value="all">Todos</option>
      </select>
      <input
        type="text"
        value={actionInput}
        onChange={(e) => setActionInput(e.target.value)}
        placeholder="Ação"
        aria-label="Filtrar por ação"
        className="w-28 rounded-lg border border-border bg-surface px-3 py-1.5 text-xs text-text outline-none placeholder:text-text-tertiary focus:border-accent"
      />
      <input
        type="text"
        value={entityInput}
        onChange={(e) => setEntityInput(e.target.value)}
        placeholder="Entidade"
        aria-label="Filtrar por entidade"
        className="w-28 rounded-lg border border-border bg-surface px-3 py-1.5 text-xs text-text outline-none placeholder:text-text-tertiary focus:border-accent"
      />
      <input
        type="date"
        value={filters.from}
        onChange={(e) => updateFilter("from", e.target.value)}
        aria-label="De"
        className="rounded-lg border border-border bg-surface px-3 py-1.5 text-xs text-text outline-none focus:border-accent"
      />
      <input
        type="date"
        value={filters.to}
        onChange={(e) => updateFilter("to", e.target.value)}
        aria-label="Até"
        className="rounded-lg border border-border bg-surface px-3 py-1.5 text-xs text-text outline-none focus:border-accent"
      />
    </div>
  );

  return (
    <AdminPage
      eyebrow="Governança"
      title="Auditoria"
      description="Toda escrita feita por operadores e pelas rotinas automáticas da plataforma, com quem fez e o que mudou. Por padrão mostra a equipe e o sistema; troque para 'Sistema' para ver só as rotinas automáticas, ou 'Todos' para investigar a atividade de qualquer usuário(a)."
      action={filterBar}
    >
      {error && (
        <div className="mb-6 rounded-2xl border border-danger/30 bg-danger/10 p-4 text-sm text-text">
          {error}
        </div>
      )}

      {loading ? (
        <div className="grid place-items-center rounded-3xl border border-border bg-surface py-20">
          <Loader2 size={20} className="animate-spin text-text-tertiary" aria-hidden />
        </div>
      ) : entries.length === 0 ? (
        <div className="rounded-3xl border border-border bg-surface py-12 text-center">
          <ScrollText size={28} strokeWidth={1.5} className="mx-auto text-text-tertiary" aria-hidden />
          <p className="mt-4 text-sm font-medium text-text">Nenhum registro encontrado.</p>
          <p className="mt-1 text-xs text-text-tertiary">
            Ajuste os filtros para ampliar a busca.
          </p>
        </div>
      ) : (
        <>
          <ul className="divide-y divide-border overflow-hidden rounded-3xl border border-border bg-surface">
            {entries.map((entry) => (
              <li key={entry.id} className="flex items-start gap-3 px-5 py-3.5">
                <span
                  className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-accent-soft text-accent"
                  aria-hidden
                >
                  <ScrollText size={16} strokeWidth={2} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm leading-relaxed text-text">
                    <span className="font-medium">
                      {entry.system ? "Sistema" : entry.actor?.name || entry.actor?.email || "—"}
                    </span>{" "}
                    {describeEntry(entry)}
                  </p>
                  <p className="mt-0.5 truncate text-[11px] text-text-tertiary">
                    {formatDateTime(entry.created_at)}
                    {!entry.system && entry.actor?.email && ` · ${entry.actor.email}`}
                  </p>
                </div>
              </li>
            ))}
          </ul>

          {cursor && (
            <div className="mt-6 flex justify-center">
              <button
                type="button"
                onClick={loadMore}
                disabled={loadingMore}
                className="inline-flex items-center gap-1.5 rounded-full border border-border bg-surface px-4 py-2.5 text-sm font-medium text-text-secondary transition-colors hover:bg-surface-alt disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loadingMore && <Loader2 size={14} className="animate-spin" aria-hidden />}
                Carregar mais
              </button>
            </div>
          )}
        </>
      )}
    </AdminPage>
  );
}
